import React, { useCallback, useMemo } from 'react';
import { useMaxWave } from 'contexts/gameState';
import { useResults } from 'contexts/results';
import { PerkWaveStats, WaveStats } from 'utils/simulate';
import { useUpdatedState } from 'utils/hooks';
import { TIER_MULTIPLIERS, TIERS, DEFAULT_TIERS } from './ResultsTable';
import './csv.scss';

const copy = text => {
  const textArea = document.createElement('textarea');
  textArea.textContent = text;
  document.body.append(textArea);
  textArea.select();
  document.execCommand('copy');
  textArea.remove();
};

const CSV_STATS_HEADERS =
  'Stat, Average Value Among Reached, Number Reached, Percent Reached';
const CSV_WAVES_HEADERS = (tiers: number[]) =>
  `Wave, Time (s), Average Perk Multiplier, Average Wave Skips, ${tiers
    .map(
      tier => `${tier} Average Coins, ${tier} Average CPH, ${tier} Average CPW`,
    )
    .join(', ')}`;

const basicStat = (label: string, stat: number) =>
  [label, stat, '-', '-'].join(', ');
const waveStat = (label: string, stat: PerkWaveStats, count: number) =>
  [
    label,
    stat.wave / (stat.has || 1),
    stat.has,
    (stat.has * 100) / (count || 1),
  ].join(', ');
const csvWave = (waveNumber: number, wave: WaveStats, tiers: number[]) =>
  `${waveNumber}, ${wave?.time || 0}, ${wave?.perkMultiplier || 1}, ${
    wave?.waveSkips || 0
  }, ${tiers
    .map(tier =>
      [
        (wave?.coins || 0) * TIER_MULTIPLIERS[tier],
        (wave?.cph || 0) * TIER_MULTIPLIERS[tier],
        ((wave?.coins || 0) * TIER_MULTIPLIERS[tier]) / waveNumber,
      ].join(', '),
    )
    .join(', ')}`;

const CsvTables: React.FC<{}> = () => {
  const results = useResults();
  const maxWave = useMaxWave();

  const [showTiers, setShowTiers] = useUpdatedState(DEFAULT_TIERS, 'showTiers');

  const updateShowTiers = useCallback(
    ({ target: { value, checked } }) => {
      setShowTiers(tiers => {
        let newTiers = [...tiers];
        if (!checked) {
          newTiers = newTiers.filter(i => i !== parseInt(value, 10));
        } else if (!newTiers.includes(value)) {
          newTiers.push(parseInt(value, 10));
        }
        newTiers.sort((a, b) => a - b);
        return newTiers;
      });
    },
    [setShowTiers],
  );

  const waveThresholds: number[] = useMemo(() => {
    const thresholds: number[] = [];
    for (let i = 100; i < maxWave; i += 100) thresholds.push(i);
    thresholds.push(maxWave);
    return thresholds;
  }, [maxWave]);
  const { count, stats } = results;

  const csvStats = useMemo(
    () =>
      [
        CSV_STATS_HEADERS,
        basicStat('Basic coin bonuses', stats.finalStats.basicCoinBonuses),
        basicStat('Defense bonuses', stats.finalStats.defenseBonuses),
        basicStat('HP bonuses', stats.finalStats.hpBonuses),
        basicStat('Extra orbs', stats.finalStats.extraOrbs),
        basicStat('Perk multiplier', stats.finalStats.perkMultiplier),
        basicStat('Perk count', stats.finalStats.perkCount),
        waveStat(
          'First wave reduction perk wave',
          stats.perkWaveStats.firstPerkReduction,
          count,
        ),
        waveStat(
          'Second wave reduction perk wave',
          stats.perkWaveStats.secondPerkReduction,
          count,
        ),
        waveStat(
          'Third wave reduction perk wave',
          stats.perkWaveStats.thirdPerkReduction,
          count,
        ),
        waveStat(
          'Coin tradeoff wave',
          stats.perkWaveStats.tradeoffCoinWave,
          count,
        ),
        waveStat(
          'Damage tradeoff wave',
          stats.perkWaveStats.damageDamageWave,
          count,
        ),
        waveStat(
          'Golden tower bonus wave',
          stats.perkWaveStats.goldenTowerWave,
          count,
        ),
        waveStat(
          '5x basic coin bonuses wave',
          stats.perkWaveStats.coinBonusesWave,
          count,
        ),
        waveStat(
          'Game speed bonus wave',
          stats.perkWaveStats.gameSpeedWave,
          count,
        ),
        waveStat(
          'All coin bonus perks wave',
          stats.perkWaveStats.allCoinBonuses,
          count,
        ),
        waveStat(
          'All needed perks wave',
          stats.perkWaveStats.allNeededPerks,
          count,
        ),
        waveStat(
          'No more options wave',
          stats.perkWaveStats.noMoreOptions,
          count,
        ),
      ].join('\n'),
    [stats, count],
  );

  const csvWaves = useMemo(
    () =>
      [
        CSV_WAVES_HEADERS(showTiers),
        ...waveThresholds.map(threshold =>
          csvWave(threshold, stats.waves[threshold], showTiers),
        ),
      ].join('\n'),
    [showTiers, stats.waves, waveThresholds],
  );

  const copyStats = useCallback(() => {
    copy(csvStats);
  }, [csvStats]);
  const copyWaves = useCallback(() => {
    copy(csvWaves);
  }, [csvWaves]);

  return (
    <div id='csv'>
      <div className='tiers'>
        Show tiers:{' '}
        {TIERS.map(tier => (
          <label htmlFor={`show-tier-${tier}`} key={tier}>
            {tier}
            <input
              id={`show-tier-${tier}`}
              type='checkbox'
              value={tier}
              checked={showTiers.includes(parseInt(tier, 10))}
              onChange={updateShowTiers}
            />
          </label>
        ))}
      </div>
      <div className='results-section'>
        <div className='title'>
          Stats{' '}
          <button type='button' onClick={copyStats}>
            Copy
          </button>
        </div>
        <pre className='csv-stats' id='stats-section'>
          {csvStats}
        </pre>
      </div>
      <div className='results-section'>
        <div className='title'>
          Waves{' '}
          <button type='button' onClick={copyWaves}>
            Copy
          </button>
        </div>
        <pre className='csv-stats' id='waves-section'>
          {csvWaves}
        </pre>
      </div>
    </div>
  );
};

export default CsvTables;
